import {Button, ButtonGroup, HStack, Icon, Stack, Text, Textarea, useDisclosure} from "@chakra-ui/react";
import {ArrowUpTrayIcon, DocumentCheckIcon, ShieldCheckIcon} from "@heroicons/react/16/solid";
import {ComponentProps, memo, useCallback, useState} from "react";
import {withDialog} from "../../../../../../../../state/withDialog";
import {DialogHeader, DialogTitle, DialogBody, DialogFooter} from "../../../../../../../../components/ui/dialog";
import {Alert} from "../../../../../../../../components/ui/alert";
import {ClientDiagram} from "../../utility/types";
import {
  DiagramId,
  PresetDiagram,
  PresetDiagramConnection,
  PresetDiagramConnectionAnchor,
  PresetDiagramNode,
} from "platformed-client/types";
import {useTemporary} from "../../../../../../../../hooks/useTemporary";
import {Tooltip} from "../../../../../../../../components/ui/tooltip";
import {usePromiseState} from "../../../../../../../../hooks/promiseState";
import api from "../../../../../../../../api";
import {HTTPError} from "platformed-client";

const TOOLTIP_POSITIONING: ComponentProps<typeof Tooltip>["positioning"] = {placement: "top"};

const JsonImportDialog = withDialog(
  memo(({onClose, diagramId}: {onClose: () => void; diagramId: DiagramId}) => {
    const [json, setJson] = useState("");

    const [submitting, submit] = usePromiseState(async () => {
      const rawJson = JSON.parse(json);

      try {
        await api.tprm.diagrams.importFromPreset(
          diagramId,
          rawJson as PresetDiagram, // backend immediately parses this
        );
      } catch (err: unknown) {
        // Do Dylan a favour and print backend parse errors in the UI
        if (err instanceof HTTPError && err.response.status === 422) {
          const message = await err.response.text();
          throw new Error(message);
        }

        throw err;
      }

      onClose();
    }, [diagramId, json, onClose]);

    return (
      <>
        <DialogHeader>
          <DialogTitle>Import data flow from JSON</DialogTitle>
        </DialogHeader>
        <DialogBody fontSize="sm">
          <Stack gap={6}>
            <Text>
              Paste data flow JSON representation into this box. You can convert an existing diagram to JSON by clicking
              the "Export JSON" button in internal mode.
            </Text>
            <Alert status="warning" title="Importing a data flow will clear all existing data!" />
            <Textarea
              minH="12lh"
              fontFamily="monospace"
              fontSize="xs"
              spellCheck={false}
              value={json}
              onChange={ev => setJson(ev.target.value)}
            />
            {submitting.lastError != null && (
              <Alert status="error" title="An error occurred">{`${submitting.lastError}`}</Alert>
            )}
          </Stack>
        </DialogBody>
        <DialogFooter>
          <HStack gap="3">
            <Button variant="outline" onClick={onClose} disabled={submitting.inProgress}>
              Cancel
            </Button>
            <Button colorPalette="green" disabled={json.length === 0} onClick={submit} loading={submitting.inProgress}>
              <ArrowUpTrayIcon />
              Import
            </Button>
          </HStack>
        </DialogFooter>
      </>
    );
  }),
  {lazyMount: true, unmountOnExit: true, placement: "center"},
);

function convertDiagramToPresetJson(diagram: ClientDiagram): PresetDiagram {
  const nodes = [...diagram.nodes.values()].map<PresetDiagramNode>(node => ({
    name: node.id,
    label: node.label,
    description: node.description,
    icon: node.icon,
    position: node.position.tuple(),
  }));

  const connections = [...diagram.connections.values()].map<PresetDiagramConnection>(connection => ({
    source_node_name: connection.from,
    target_node_name: connection.to,
    label: connection.label,
    color: connection.color,
    anchors: connection.anchors.map<PresetDiagramConnectionAnchor>(anchor => ({position: anchor.position.tuple()})),
  }));

  return {
    name: diagram.name,
    description: "",
    diagram_type: "ProcessFlow",
    summary: null,
    lanes: [],
    nodes,
    connections,
  };
}

export const JsonImportExport = memo(({diagramId, diagram}: {diagramId: DiagramId; diagram: ClientDiagram}) => {
  const importDialog = useDisclosure();
  const [justExported, triggerJustExported] = useTemporary(1500);

  const handleClickExport = useCallback(async () => {
    const preset = convertDiagramToPresetJson(diagram);
    const json = JSON.stringify(preset, null, 2);
    await navigator.clipboard.writeText(json);
    triggerJustExported();
  }, [diagram, triggerJustExported]);

  return (
    <>
      <HStack
        pointerEvents="auto"
        bg="white"
        shadow="md"
        rounded="md"
        outline="solid"
        outlineColor="red.500"
        outlineWidth="1px"
        gap={0}
      >
        <HStack px={2} color="fg.error">
          <Icon asChild>
            <ShieldCheckIcon />
          </Icon>
          <Text fontSize="2xs" fontWeight="semibold">
            INTERNAL
          </Text>
        </HStack>
        <ButtonGroup attached>
          <Button size="xs" variant="subtle" colorPalette="blue" onClick={importDialog.onOpen}>
            Import from JSON
          </Button>
          <Tooltip
            content={
              <HStack>
                <Icon asChild>
                  <DocumentCheckIcon />
                </Icon>
                <Text>Copied to clipboard!</Text>
              </HStack>
            }
            open={justExported}
            positioning={TOOLTIP_POSITIONING}
          >
            <Button size="xs" variant="subtle" colorPalette="green" onClick={handleClickExport} disabled={justExported}>
              Export to JSON
            </Button>
          </Tooltip>
        </ButtonGroup>
      </HStack>
      <JsonImportDialog {...importDialog} diagramId={diagramId} />
    </>
  );
});
